const printLabels = (selectedDocuments, labelSize, labelShape = null) => {
  if (selectedDocuments.length === 0) {
    alert("Please select at least one document to generate labels.");
    return;
  }

  const formatOptions = {
    separatorType: "newline",
    customSeparator: "",
    lineBreakAfter: [],
    enableCustomLineBreaks: false,
  };

  const layoutOptions = {
    labelsPerRow: 3,
    horizontalSpacing: 10,
    verticalSpacing: 30,
    fontSize: 14,
    lineHeight: 1.2,
  };

  const currentLabelShape =
    labelShape || localStorage.getItem("documentLabelShape") || "circle";

  const currentLabelSize =
    labelSize ||
    parseInt(localStorage.getItem("documentLabelSize"), 10) ||
    parseInt(localStorage.getItem("documentLabelCircleSize"), 10) ||
    150;

  const loadedFormatOptions = getStoredOptions(
    "documentLabelFormatOptions",
    formatOptions
  );
  const loadedLayoutOptions = getStoredOptions(
    "documentLabelLayoutOptions",
    layoutOptions
  );

  function getStoredOptions(key, defaults) {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        return key === "documentLabelFormatOptions"
          ? {
              separatorType: parsed.separatorType || defaults.separatorType,
              customSeparator:
                parsed.customSeparator || defaults.customSeparator,
              lineBreakAfter: parsed.lineBreakAfter || defaults.lineBreakAfter,
              enableCustomLineBreaks:
                parsed.enableCustomLineBreaks ||
                defaults.enableCustomLineBreaks,
            }
          : { ...defaults, ...parsed };
      }
    } catch (error) {
      // Silent error handling
    }
    return defaults;
  }

  function getLabelDimensions(shape, size) {
    if (shape === "circle") {
      return { width: size, height: size, borderRadius: "50%" };
    }
    const width = size;
    const height = (size * 4) / 3;
    return { width, height, borderRadius: "8px" };
  }

  function getCallNumber(document) {
    if (document.callNumberWithCopy) {
      return document.callNumberWithCopy;
    }

    const callNumberOptions = [
      document?.call_number,
      document?.metadata?.call_number,
      document?.metadata?.callNumber,
      document?.callNumber,
      document?.metadata?.call_number_text,
      document?.metadata?.classification?.call_number,
    ];

    for (const option of callNumberOptions) {
      if (option && option !== "N/A" && option.toString().trim() !== "") {
        return option.toString();
      }
    }
    return "N/A";
  }

  function getTitle(document) {
    const titleOptions = [
      document?.documentInfo?.title,
      document?.title,
      document?.metadata?.title,
      document?.name,
      document?.metadata?.name,
      document?.metadata?.title_text,
    ];

    for (const option of titleOptions) {
      if (option && option.toString().trim() !== "") {
        return option.toString();
      }
    }
    return "Untitled Document";
  }

  function formatCallNumber(callNumber, options = loadedFormatOptions) {
    if (!callNumber || callNumber === "N/A") {
      return ["N/A"];
    }

    let parts = callNumber
      .toString()
      .split(" ")
      .filter((part) => part.trim() !== "");

    parts = parts.filter((part, index) => {
      if (
        part === "-" &&
        index < parts.length - 1 &&
        parts[index + 1].match(/^c\d+$/i)
      ) {
        return false;
      }
      return true;
    });

    if (!options.enableCustomLineBreaks) {
      switch (options.separatorType) {
        case "newline":
          return parts;
        case "dash":
          return [parts.join("-")];
        case "plus":
          return [parts.join("+")];
        case "space":
          return [parts.join(" ")];
        case "custom":
          return [parts.join(options.customSeparator || "-")];
        default:
          return parts;
      }
    } else {
      const result = [];
      let currentLine = [];

      parts.forEach((part, index) => {
        currentLine.push(part);
        if (
          options.lineBreakAfter.includes(index) ||
          index === parts.length - 1
        ) {
          const separator =
            options.separatorType === "custom"
              ? options.customSeparator || "-"
              : { dash: "-", plus: "+", space: " " }[options.separatorType] ||
                " ";
          result.push(currentLine.join(separator));
          currentLine = [];
        }
      });

      return result.length > 0 ? result : parts;
    }
  }

  function generateLabelsHTML() {
    return selectedDocuments
      .map((doc, index) => {
        const callNumber = getCallNumber(doc);
        const title = getTitle(doc);
        const callNumberParts = formatCallNumber(callNumber);
        const callNumberHTML = callNumberParts
          .map((part) => `<div class="call-number-part">${part}</div>`)
          .join("");
        const copyInfo = doc.copyNumber ? ` (Copy ${doc.copyNumber})` : "";

        return `
        <div class="label-container" data-label-index="${index}">
          <div class="call-number-label call-number-${currentLabelShape}" style="
            width: ${labelDimensions.width}px;
            height: ${labelDimensions.height}px;
            border-radius: ${labelDimensions.borderRadius};
            border: 2px solid #000;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            margin: 10px;
            padding: 10px;
            box-sizing: border-box;
            font-size: ${loadedLayoutOptions.fontSize}px;
            line-height: ${loadedLayoutOptions.lineHeight};
            background-color: white;
          ">
            ${callNumberHTML}
          </div>
          <div class="document-title">${title}${copyInfo}</div>
        </div>`;
      })
      .join("");
  }

  const labelDimensions = getLabelDimensions(
    currentLabelShape,
    currentLabelSize
  );
  const maxLabelDimension = Math.max(
    labelDimensions.width,
    labelDimensions.height
  );
  const isSelectiveCopyMode = selectedDocuments.some(
    (doc) => doc.copyNumber !== undefined
  );
  const printModeDescription = isSelectiveCopyMode
    ? `Selected Copies (${selectedDocuments.length} labels)`
    : `All Documents (${selectedDocuments.length} labels)`;

  const printTab = window.open("", "_blank");

  const printStyles = `
    @media print {
      body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
      .labels-grid { display: flex; flex-wrap: wrap; justify-content: flex-start; gap: ${
        loadedLayoutOptions.horizontalSpacing
      }px; }
      .label-container { 
        display: flex; flex-direction: column; align-items: center; 
        margin-bottom: ${
          loadedLayoutOptions.verticalSpacing
        }px; page-break-inside: avoid;
        width: calc(${100 / loadedLayoutOptions.labelsPerRow}% - ${
    (loadedLayoutOptions.horizontalSpacing *
      (loadedLayoutOptions.labelsPerRow - 1)) /
    loadedLayoutOptions.labelsPerRow
  }px);
        box-sizing: border-box; padding: 10px; min-height: ${
          labelDimensions.height + 50
        }px;
      }
      .document-title { margin-top: 5px; font-size: 12px; text-align: center; max-width: ${maxLabelDimension}px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .call-number-part { text-align: center; line-height: ${
        loadedLayoutOptions.lineHeight
      }; word-wrap: break-word; hyphens: auto; color: #000; font-weight: normal; }
      .call-number-label { flex-shrink: 0; background-color: white !important; }
      .call-number-rectangle { word-spacing: -0.1em; }
      ${
        loadedLayoutOptions.labelsPerRow === 1
          ? ".label-container { page-break-after: auto; }"
          : ""
      }
      ${
        loadedLayoutOptions.labelsPerRow > 4
          ? ".label-container { font-size: 0.9em; }"
          : ""
      }
    }
    @media screen {
      body { font-family: Arial, sans-serif; margin: 20px; background-color: #f5f5f5; }
      h1 { color: #333; border-bottom: 2px solid #ddd; padding-bottom: 10px; }
      .labels-grid { background-color: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
      .call-number-part { color: #000; font-weight: normal; }
      ${
        isSelectiveCopyMode
          ? `
        .label-container { position: relative; }
        .label-container::before { 
          content: "Selected Copy"; position: absolute; top: -5px; right: -5px;
          background-color: #4CAF50; color: white; padding: 2px 6px; font-size: 10px; border-radius: 4px; font-weight: bold;
        }`
          : ""
      }
    }`;

  const printScript = `
    window.onload = function() {
      setTimeout(function() { window.print(); }, 100);
      window.onafterprint = function() {
        setTimeout(function() {
          if (window.opener && !window.opener.closed) { window.opener.focus(); }
          window.close();
        }, 500);
      };
      window.onfocus = function() {
        setTimeout(function() {
          if (window.opener && !window.opener.closed) { window.opener.focus(); }
          window.close();
        }, 100);
      };
    };`;

  printTab.document.write(`
    <html>
      <head>
        <title>Document Labels - Print (${
          currentLabelShape === "circle" ? "Circle" : "Rectangle"
        })</title>
        <style>${printStyles}</style>
      </head>
      <body>
        <h1 style="text-align: center; margin-bottom: 20px;">
          Document Labels - ${
            currentLabelShape === "circle" ? "Circle" : "Rectangle"
          } Format
          <br>
          <small style="font-size: 0.6em; color: #666;">
            ${
              currentLabelShape === "circle"
                ? `${currentLabelSize}px diameter`
                : `${labelDimensions.width}×${labelDimensions.height}px`
            } | 
            ${
              loadedLayoutOptions.labelsPerRow
            } per row | ${printModeDescription}
          </small>
        </h1>
        <div class="labels-grid">${generateLabelsHTML()}</div>
        <script>${printScript}</script>
      </body>
    </html>
  `);

  printTab.document.close();
};

export default { printLabels };
